import React, { useEffect, useState } from 'react';
import { useAppSelector } from 'hooks';
import { Collapse, Input, Table } from 'reactstrap';
import { InputType } from 'reactstrap/types/lib/Input';

import { showNDISRates } from '~appointments/lib/show';

import { InputProps } from '~components/form-fields';

import { selectAppointmentServiceTypeById } from '~appointments/selectors/serviceTypes';
import { selectNDISSupportItems } from '~lookups/selectors/ndisSupportItems';

function FormInputNDISRates({ id, value = {}, onChange }: InputProps) {
  const services = [
    {
      id: 'day',
      caption: 'Weekday Daytime/Individual Code',
    },
    {
      id: 'eve',
      caption: 'Weekday Evening',
    },
    {
      id: 'night',
      caption: 'Weekday Night',
    },
    {
      id: 'saturday',
      caption: 'Saturday',
    },
    {
      id: 'sunday',
      caption: 'Sunday',
    },
    {
      id: 'ph',
      caption: 'Public Holiday',
    },
    {
      id: 'cancellation',
      caption: 'Cancellation',
    },
    {
      id: 'providertravel',
      caption: 'Provider Travel',
    },
  ];

  const serviceType = useAppSelector((state) =>
    selectAppointmentServiceTypeById(state, parseInt(value.service_type_id)),
  );
  const ndisItems = useAppSelector(selectNDISSupportItems);

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const { client_id } = value;
    if (serviceType) {
      if (serviceType.ndis_support_item > 0 && client_id) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }
  }, [value.service_type_id, value.client_id]);

  useEffect(() => {
    if (Object.keys(ndisItems).length > 0) {
      const newValue: { [key: string]: string | number } = {};
      Object.entries(ndisItems).forEach(([key, value]) => (newValue[key] = value));
      onChange && onChange(id, newValue);
    }
  }, [ndisItems]);

  const initialiseValue = () => {
    let newValue: { [key: string]: string | number } = {};
    if (value instanceof Object) newValue = { ...value };
    delete newValue.client_id;
    delete newValue.service_type_id;
    return newValue;
  };

  const setChange = (target: { name: string; value: any }) => {
    const { name, value: targetValue } = target;
    const newValue = initialiseValue();
    newValue[name] = targetValue;
    onChange && onChange(id, newValue);
  };

  const buildInput = (id: string, type: InputType, ariaLabel: string) => (
    <Input
      type={type}
      name={id}
      id={id}
      value={value[id] ? value[id] : ''}
      onChange={({ target: { name, value } }) => {
        setChange({ name, value });
      }}
      aria-label={ariaLabel}
      data-cy={`${id}-input`}
    />
  );

  // Render a row for each category the item is actually associated with — i.e. the categories
  // returned by the API for this support item (derived from its service_link group). Time-of-day
  // categories (day/eve/night/saturday/sunday/ph) and modifier categories (cancellation/providertravel)
  // are mutually exclusive per item, so only the relevant set shows. Association is decided by key
  // presence: the API emits a `${id}_rate`/`${id}_code` pair for every associated category (blank
  // values included) and omits the pair entirely for categories the item is not associated with —
  // so a present-but-blank category still renders, and unassociated categories are hidden. Falls
  // back to the saved value when there is no fresh lookup (e.g. editing an existing appointment).
  const associatedRates = Object.keys(ndisItems).length > 0 ? ndisItems : value;
  const visibleServices = services.filter(
    ({ id }) => `${id}_rate` in associatedRates || `${id}_code` in associatedRates,
  );

  // When the item is a modifier parent (its group exposes modifier categories), the 'day' row is
  // the item's Direct Service rather than a weekday-daytime rate, so label it accordingly. This is
  // display only — the field id/keys (day_rate/day_code) are unchanged, so saving is unaffected.
  const modifierIds = ['cancellation', 'providertravel'];
  const isDirectService = modifierIds.some(
    (modifierId) => `${modifierId}_rate` in associatedRates || `${modifierId}_code` in associatedRates,
  );

  const bodyRows = visibleServices.map(({ id, caption }) => {
    const rowLabel = id === 'day' && isDirectService ? 'Direct Service' : caption;
    return (
      <tr key={id}>
        <td className="align-middle">{rowLabel}</td>
        <td className="d-flex align-items-center">
          <span className="me-2">$</span>
          <span className="w-50">{buildInput(`${id}_rate`, 'number', `${rowLabel} rate`)}</span>
          <span className="ms-2">/ hour</span>
        </td>
        <td className="align-middle">{buildInput(`${id}_code`, 'text', `${rowLabel} code`)}</td>
      </tr>
    );
  });

  const headerClassName = 'ps-2 pe-2 border-bottom';

  const shouldShowNDISRates = showNDISRates(value);

  return shouldShowNDISRates ? (
    <Collapse isOpen={isOpen}>
      <Table data-cy={`${id}-table`}>
        <thead>
          <tr>
            <th className={headerClassName}>Service</th>
            <th className={headerClassName}>Rate</th>
            <th className={headerClassName}>Code</th>
          </tr>
        </thead>
        <tbody>{bodyRows}</tbody>
      </Table>
    </Collapse>
  ) : null;
}

export default FormInputNDISRates;
